import { API_TAGS } from '../../constants/api';
import { apiSlice } from '../apiSlice';
import { MESSAGES } from './automatedTestsMessages';

export const automatedTestsSlice = apiSlice.injectEndpoints({
  endpoints: (builder => ({
    fetchAutomatedTests: builder.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/automated-tests`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_AUTOMATED_TESTS.ERROR
        }
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.items.map(({ id }) => ({ type: API_TAGS.AUTOMATED_TESTS, id })),
            { type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }
          ]
          : [{ type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }]
    }),

    fetchAutomatedTestById: builder.query({
      query: ({ orgId, id }) => ({
        url: `organizations/${orgId}/automated-tests/${id}`,
        method: 'GET',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_AUTOMATED_TEST.ERROR
        }
      }),
      providesTags: (result, error, { id }) => [{ type: API_TAGS.AUTOMATED_TESTS, id }]
    }),

    createAutomatedTests: builder.mutation({
      query: ({ orgId, data }) => ({
        url: `organizations/${orgId}/automated-tests`,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_AUTOMATED_TEST.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_AUTOMATED_TEST.SUCCESS
        }
      }),
      invalidatesTags: [{ type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }]
    }),

    updateAutomatedTest: builder.mutation({
      query: ({ orgId, id, data }) => ({
        url: `organizations/${orgId}/automated-tests/${id}`,
        method: 'PATCH',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_AUTOMATED_TEST.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_AUTOMATED_TEST.SUCCESS
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: API_TAGS.AUTOMATED_TESTS, id },
        { type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }
      ]
    }),

    deleteAutomatedTest: builder.mutation({
      query: ({ orgId, id }) => ({
        url: `organizations/${orgId}/automated-tests/${id}`,
        method: 'DELETE',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_AUTOMATED_TEST.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_AUTOMATED_TEST.SUCCESS
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: API_TAGS.AUTOMATED_TESTS, id },
        { type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }
      ]
    }),

    runAutomatedTest: builder.mutation({
      query: ({ orgId, id }) => ({
        url: `organizations/${orgId}/automated-tests/${id}/run`,
        method: 'POST',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.RUN_AUTOMATED_TEST.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.RUN_AUTOMATED_TEST.SUCCESS
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: API_TAGS.AUTOMATED_TESTS, id },
        { type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }
      ]
    }),

    cloneAutomatedTest: builder.mutation({
      query: ({ orgId, id, data }) => ({
        url: `organizations/${orgId}/automated-tests/${id}/clone`,
        method: 'POST',
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CLONE_AUTOMATED_TEST.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.CLONE_AUTOMATED_TEST.SUCCESS
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: API_TAGS.AUTOMATED_TESTS, id: 'LIST' }
      ]
    }),

    fetchAutomatedTestResults: builder.query({
      query: ({ orgId, urlParams = {} }) => ({
        url: `organizations/${orgId}/automated-test-results`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_AUTOMATED_TEST_RESULTS.ERROR
        }
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.items.map(({ id }) => ({ type: API_TAGS.AUTOMATED_TESTS_RESULTS, id })),
            { type: API_TAGS.AUTOMATED_TESTS_RESULTS, id: 'LIST' }
          ]
          : [{ type: API_TAGS.AUTOMATED_TESTS_RESULTS, id: 'LIST' }]
    }),

    fetchAutomatedTestResultById: builder.query({
      query: ({ orgId, id }) => ({
        url: `organizations/${orgId}/automated-test-results/${id}`,
        method: 'GET',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_AUTOMATED_TEST_RESULT.ERROR
        }
      }),
      providesTags: (result, error, { id }) => [{ type: API_TAGS.AUTOMATED_TESTS_RESULTS, id }]
    }),

    deleteAutomatedTestResult: builder.mutation({
      query: ({ orgId, id }) => ({
        url: `organizations/${orgId}/automated-test-results/${id}`,
        method: 'DELETE',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_AUTOMATED_TEST_RESULT.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_AUTOMATED_TEST_RESULT.SUCCESS
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: API_TAGS.AUTOMATED_TESTS_RESULTS, id },
        { type: API_TAGS.AUTOMATED_TESTS_RESULTS, id: 'LIST' }
      ]
    })
  }))
});

export const {
  useFetchAutomatedTestsQuery,
  useFetchAutomatedTestByIdQuery,
  useCreateAutomatedTestsMutation,
  useUpdateAutomatedTestMutation,
  useDeleteAutomatedTestMutation,
  useRunAutomatedTestMutation,
  useCloneAutomatedTestMutation,
  useFetchAutomatedTestResultsQuery,
  useFetchAutomatedTestResultByIdQuery,
  useDeleteAutomatedTestResultMutation
} = automatedTestsSlice;
